import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { berlinDate, PAY_RHYTHM_LABELS, RUN_PERIOD_LABELS, RUN_PERIODS, defaultPayDate, latestMinimumWagePayDate, type PayrollRunDetail, type PayrollSetup, type RunPeriod } from '@kontor/shared';
import { api, ApiError } from '../../api';
import { dayDe } from './PayrollPage';
import './payroll.css';

const addDays = (iso: string, n: number) => new Date(new Date(`${iso}T12:00:00Z`).getTime() + n * 86_400_000).toISOString().slice(0, 10);
const mondayOf = (iso: string) => addDays(iso, -((new Date(`${iso}T12:00:00Z`).getUTCDay() + 6) % 7));
const lastOfMonth = (month: string) => new Date(Date.UTC(Number(month.slice(0, 4)), Number(month.slice(5, 7)), 0)).toISOString().slice(0, 10);

/** Neuen Lohnlauf anlegen: Art, Zeitraum und Auszahlungstag. Der Auszahlungstag kommt aus der Einrichtung und wird gegen den spätesten Termin nach dem Mindestlohngesetz geprüft. */
export function PayrollRunFormPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const setup = useQuery({ queryKey: ['payroll-setup'], queryFn: async () => (await api<{ data: PayrollSetup }>('GET', '/payroll/setup')).data });
  const today = berlinDate();
  const prevMonth = new Date(Date.UTC(Number(today.slice(0, 4)), Number(today.slice(5, 7)) - 2, 1)).toISOString().slice(0, 7);

  const [period, setPeriod] = useState<RunPeriod>((params.get('period') as RunPeriod) || 'month');
  const [month, setMonth] = useState((params.get('start') ?? '').slice(0, 7) || today.slice(0, 7));
  const [date, setDate] = useState(params.get('start') ?? today);
  const [payDate, setPayDate] = useState('');
  const [payTouched, setPayTouched] = useState(false);
  const [note, setNote] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const range = useMemo(() => {
    if (period === 'month') return { start: `${month}-01`, end: lastOfMonth(month) };
    if (period === 'week') return { start: mondayOf(date), end: addDays(mondayOf(date), 6) };
    return { start: date, end: date };
  }, [period, month, date]);

  // Vorschlag für den Auszahlungstag, solange der Benutzer ihn nicht selbst geändert hat
  useEffect(() => {
    if (!payTouched && setup.data) setPayDate(defaultPayDate(period, range.start, range.end, setup.data.settings.payDay));
  }, [period, range.start, range.end, setup.data, payTouched]);
  void prevMonth;

  const deadline = latestMinimumWagePayDate(range.end);
  const late = period === 'month' && payDate > deadline;

  const create = useMutation({
    mutationFn: async () => (await api<{ data: PayrollRunDetail }>('POST', '/payroll/runs', { period, periodStart: range.start, periodEnd: range.end, payDate, note: note.trim() || null })).data,
    onSuccess: (run) => navigate(`/loehne/${run.id}`, { state: { created: run.warnings } }),
    onError: (e) => setErrors(e instanceof ApiError && e.status === 422 ? e.fields : { _: e instanceof Error ? e.message : 'Der Lohnlauf konnte nicht angelegt werden.' }),
  });
  const err = (key: string) => (errors[key] ? <small className="field__error" role="alert">{errors[key]}</small> : null);

  return (
    <>
      <p><Link to="/loehne" className="back-link">← Löhne</Link></p>
      <div className="page-head"><h1>Neuer Lohnlauf</h1></div>
      <form className="card" onSubmit={(e: FormEvent) => { e.preventDefault(); setErrors({}); create.mutate(); }} noValidate>
        {errors._ && <div className="alert alert--error" role="alert">{errors._}</div>}
        <p className="muted">Der Lauf nimmt alle aktiven Mitarbeiter mit Vergütung auf, deren Zahlungsrhythmus zur Art passt. Mitarbeiter, die in dem Zeitraum schon in einem anderen Lauf stehen, bleiben draußen. Der Lauf ist zunächst ein Entwurf.</p>
        <div className="form-grid form-grid--aligned">
          <label className="field">
            <span>Art</span>
            <select className="input" value={period} onChange={(e) => { setPeriod(e.target.value as RunPeriod); setPayTouched(false); }}>
              {RUN_PERIODS.map((p) => <option key={p} value={p}>{RUN_PERIOD_LABELS[p]}{p === 'month' ? ` (${PAY_RHYTHM_LABELS.monthly}, nach Vereinbarung)` : p === 'week' ? ` (${PAY_RHYTHM_LABELS.weekly})` : ` (${PAY_RHYTHM_LABELS.daily})`}</option>)}
            </select>
          </label>
          {period === 'month' ? (
            <label className="field"><span>Monat</span><input className="input" type="month" value={month} onChange={(e) => { setMonth(e.target.value); setPayTouched(false); }} /></label>
          ) : (
            <label className="field"><span>{period === 'week' ? 'Ein Tag der Woche' : 'Tag'}</span><input className="input" type="date" value={date} onChange={(e) => { setDate(e.target.value); setPayTouched(false); }} /></label>
          )}
          <label className="field">
            <span>Zeitraum</span>
            <input className="input" value={period === 'day' ? dayDe(range.start) : `${dayDe(range.start)} – ${dayDe(range.end)}`} readOnly />
          </label>
          <label className="field">
            <span>Auszahlung am</span>
            <input className="input" type="date" value={payDate} onChange={(e) => { setPayDate(e.target.value); setPayTouched(true); }} aria-invalid={!!errors.payDate} />
            {err('payDate')}
          </label>
          <label className="field field--wide"><span>Notiz (nur intern)</span><input className="input" value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} /></label>
        </div>
        {late && <div className="alert alert--warn" role="alert">Der späteste Termin nach dem Mindestlohngesetz ist der {dayDe(deadline)} (letzter Bankarbeitstag des Folgemonats). Diese Auszahlung ginge später und lässt sich nicht anlegen.</div>}
        <div className="form-actions">
          <button className="btn btn--primary" type="submit" disabled={create.isPending || !payDate}>{create.isPending ? 'Wird angelegt …' : 'Lohnlauf anlegen'}</button>
          <Link className="btn btn--secondary" to="/loehne">Abbrechen</Link>
        </div>
      </form>
    </>
  );
}
